import { H3 } from '@frontend/shadcn/components/typography/h3';
import { faBolt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { P } from '@frontend/shadcn/components/typography/p';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { AutomationExecutionItem } from './AutomationExecutionItem';
import { AutomationSummaryProps } from '../constants';

// Constants
const SKELETON_EXECUTIONS_COUNT = 3;

// Components
const LoadingSkeleton = () => (
  <div className="sticky top-0 flex flex-col gap-4" data-testid="loading-skeleton">
    <div className="flex items-center gap-2">
      <div className="bg-grey-6 rounded-lg p-2 h-6 w-6 flex items-center justify-center">
        <FontAwesomeIcon icon={faBolt} className="text-white" />
      </div>
      <H3 className="font-medium p-0 m-0">Automations Overview</H3>
    </div>
    <Card className="border border-border shadow-none">
      <CardContent className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="flex flex-col gap-2 items-center justify-center">
              <Skeleton className="h-6 w-12" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </div>
        <Separator className="p-0 my-3" />
        <div className="flex flex-col gap-3">
          <Skeleton className="h-5 w-32" />
          {[...Array(SKELETON_EXECUTIONS_COUNT)].map((_, index) => (
            <div key={index} className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

const StatisticsGrid = ({ summary }: { summary: AutomationSummaryProps['summary'] }) => (
  <div className="grid grid-cols-2 gap-4 text-grey-6">
    <div className="flex flex-col gap-2 items-center justify-center">
      <H3 className="font-medium p-0 m-0 text-lg">{summary.automations.active}</H3>
      <P className="font-regular text-xs leading-5">Automations On</P>
    </div>
    <div className="flex flex-col gap-2 items-center justify-center">
      <H3 className="font-medium p-0 m-0 text-lg">{summary.executions.pastWeek.total}</H3>
      <P className="font-regular text-xs leading-5">Triggered This Week</P>
    </div>
    <div className="flex flex-col gap-2 items-center justify-center">
      <H3 className="font-medium p-0 m-0 text-lg">{summary.executions.allTime.failed}</H3>
      <P className="font-regular text-xs leading-5">Failed</P>
    </div>
    <div className="flex flex-col gap-2 items-center justify-center">
      <H3 className="font-medium p-0 m-0 text-lg">{summary.executions.allTime.total}</H3>
      <P className="font-regular text-xs leading-5">Total Triggered</P>
    </div>
  </div>
);

const LatestAutomations = ({ executions }: { executions: AutomationSummaryProps['executions'] }) => (
  <div className="flex flex-col gap-3">
    <H3 className="font-medium text-sm m-0">Latest Automations</H3>
    {executions.length > 0 ? (
      executions.map((execution) => (
        <AutomationExecutionItem key={execution.id} execution={execution} />
      ))
    ) : (
      <P className="text-sm font-normal text-grey-5 m-0">
        Add an automation to begin streamlining your campaign efforts.
      </P>
    )}
  </div>
);

// Main Component
export const AutomationSummary = ({ summary, executions, isLoading }: AutomationSummaryProps) => {
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="sticky top-0 flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <div className="bg-grey-6 rounded-lg p-2 h-6 w-6 flex items-center justify-center">
          <FontAwesomeIcon icon={faBolt} className="text-white" />
        </div>
        <H3 className="font-medium p-0 m-0">Automations Overview</H3>
      </div>
      <Card className="border border-border shadow-none">
        <CardContent className="p-4">
          <StatisticsGrid summary={summary} />
          <Separator className="p-0 my-3" />
          <LatestAutomations executions={executions} />
        </CardContent>
      </Card>
    </div>
  );
};
